import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import type { ComponentType } from 'react'

import type { UserRole } from '@/lib/api/contracts'

import { RedirectIfSignedIn, RouteGuard } from './guards/RouteGuard'
import { AppShell } from './layouts/AppShell'
import { NotFound } from './NotFound'

/**
 * Bir ekranı oturum arkasına alır. Sarmalayıcı burada, sayfa bileşeninin içinde değil: ekranın
 * kendisi kimin girebileceğini bilmek zorunda kalmasın, route ağacına bakan biri de bunu tek
 * yerden görebilsin.
 */
function guarded(Component: ComponentType, roles?: readonly UserRole[]) {
  return function Guarded() {
    return (
      <RouteGuard roles={roles}>
        <Component />
      </RouteGuard>
    )
  }
}

/** Oturumu olan kullanıcıyı kimlik ekranlarından kendi açılış sayfasına yollar. */
function withRedirectIfSignedIn(Component: ComponentType) {
  return function Redirected() {
    return (
      <RedirectIfSignedIn>
        <Component />
      </RedirectIfSignedIn>
    )
  }
}

const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: NotFound,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router yönlendirmeyi `redirect()` nesnesini fırlatarak yapar; bu bir hata değildir.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/login' })
  },
})

// --- Kimlik ekranları (C1–C3) -------------------------------------------------------------
// Hepsi barrel'dan değil kendi modülünden yükleniyor: barrel import'u tüm feature'ı tek parçaya
// toplar ve rota bazlı kod bölmeyi (standards §4) etkisiz kılar.

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: withRedirectIfSignedIn(
    lazyRouteComponent(() => import('@/features/auth/LoginPage'), 'LoginPage'),
  ),
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: withRedirectIfSignedIn(
    lazyRouteComponent(() => import('@/features/auth/ForgotPasswordPage'), 'ForgotPasswordPage'),
  ),
})

const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/set-password',
  component: lazyRouteComponent(() => import('@/features/auth/SetPasswordPage'), 'SetPasswordPage'),
})

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-password',
  // Guard yok: `mustChangePassword` olan kullanıcı zaten buraya yönlendiriliyor, bir de buradan
  // geri yollamak sonsuz döngü olurdu. Oturum kontrolünü ekranın kendisi yapar.
  component: lazyRouteComponent(
    () => import('@/features/auth/ChangePasswordPage'),
    'ChangePasswordPage',
  ),
})

const twoFactorSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/two-factor-setup',
  // Guard yok, `change-password` ile aynı gerekçe: zorunluluğu olan kullanıcı zaten buraya
  // yönlendiriliyor ve buradan da geri yollamak sonsuz döngü olurdu.
  component: lazyRouteComponent(
    () => import('@/features/auth/TwoFactorSetupPage'),
    'TwoFactorSetupPage',
  ),
})

// --- Profil ve hata sayfaları (C5–C6) -----------------------------------------------------

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: guarded(
    lazyRouteComponent(() => import('@/features/profile/ProfilePage'), 'ProfilePage'),
  ),
})

// Anonim: başvuran henüz müşteri değil, ve oturumu olan biri de başkası adına başvuru
// gönderebilir — bu yüzden `RedirectIfSignedIn` ile sarılmıyor (C4).
const accountRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account-request',
  component: lazyRouteComponent(
    () => import('@/features/public/AccountRequestPage'),
    'AccountRequestPage',
  ),
})

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/403',
  component: lazyRouteComponent(() => import('@/features/errors/ForbiddenPage'), 'ForbiddenPage'),
})

const maintenanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bakim',
  component: lazyRouteComponent(
    () => import('@/features/errors/MaintenancePage'),
    'MaintenancePage',
  ),
})

// Rol bazlı kod bölme: yönetim paketi tenant kullanıcısına inmez
// (docs/standards/frontend-standards.md §4).
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  component: guarded(
    lazyRouteComponent(() => import('@/features/portal/PortalHomePage'), 'PortalHomePage'),
    ['TenantAdmin', 'TenantUser'],
  ),
})

// --- Portal kullanıcı yönetimi (P7) -------------------------------------------------------
// Yalnız TenantAdmin: sıradan kullanıcı kendi ekibini yönetmez (docs/spec/07-pages-ui.md P7).

const portalUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/users',
  component: guarded(
    lazyRouteComponent(() => import('@/features/portal/users/PortalUsersPage'), 'PortalUsersPage'),
    ['TenantAdmin'],
  ),
})

const portalUserDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/users/$userId',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/users/PortalUserDetailPage'),
      'PortalUserDetailPage',
    ),
    ['TenantAdmin'],
  ),
})

const portalApiClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/api-clients',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/api-clients/PortalApiClientsPage'),
      'PortalApiClientsPage',
    ),
    ['TenantAdmin'],
  ),
})

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: guarded(
    lazyRouteComponent(() => import('@/features/admin/AdminHomePage'), 'AdminHomePage'),
    ['SuperAdmin'],
  ),
})

// --- Firma yönetimi (A2/A3) ---------------------------------------------------------------
// `/admin/tenants/new` daha spesifik olduğu için `$tenantId`'den önce tanımlanır; aksi hâlde
// "new" bir kimlik gibi okunur ve olmayan bir firma açılmaya çalışılır.

const tenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants',
  component: guarded(
    lazyRouteComponent(() => import('@/features/admin/tenants/TenantsPage'), 'TenantsPage'),
    ['SuperAdmin'],
  ),
})

const createTenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/new',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/tenants/CreateTenantPage'),
      'CreateTenantPage',
    ),
    ['SuperAdmin'],
  ),
})

const tenantDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/$tenantId',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/tenants/TenantDetailPage'),
      'TenantDetailPage',
    ),
    ['SuperAdmin'],
  ),
})

// --- Sorgu ekranları (P2/P3/P4) -----------------------------------------------------------
// `/portal/queries/new` daha spesifik olduğu için `$trackingNo`'dan önce tanımlanır; aksi hâlde
// "new" bir takip numarası gibi okunur ve olmayan bir sorgu açılmaya çalışılır.

const queryHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/queries',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/queries/QueryHistoryPage'),
      'QueryHistoryPage',
    ),

    // Sıradan kullanıcı da geçmişini görür; sunucu ona yalnız kendi satırlarını veriyor
    // (`QueryScope`), yani ekranı role göre saklamak gereksiz bir ikinci kural olurdu.
    ['TenantAdmin', 'TenantUser'],
  ),
})

const newQueryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/queries/new',
  component: guarded(
    lazyRouteComponent(() => import('@/features/portal/queries/NewQueryPage'), 'NewQueryPage'),
    ['TenantAdmin', 'TenantUser'],
  ),
})

const queryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/queries/$trackingNo',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/queries/QueryDetailPage'),
      'QueryDetailPage',
    ),
    ['TenantAdmin', 'TenantUser'],
  ),
})

// --- Toplu sorgu (P5) ------------------------------------------------------------------------
// İki rota, tek akış: yükleme ve geçmiş bir sayfada, çalışan bir yüklemenin ilerlemesi kendi
// sayfasında. Onaydan sonra ekran oraya geçer — dakikalarca sürebilen bir işi liste satırından
// izlemek, sayaçları büyüyen bir satıra bakmaktan başka bir şey olmazdı.

const batchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/batches',
  component: guarded(
    lazyRouteComponent(() => import('@/features/portal/batches/BatchPage'), 'BatchPage'),

    // Sıradan kullanıcı da toplu sorgu çalıştırır; harcamayı bakiye ve tarife sınırlar, rol değil.
    ['TenantAdmin', 'TenantUser'],
  ),
})

const batchDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/batches/$batchId',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/batches/BatchDetailPage'),
      'BatchDetailPage',
    ),
    ['TenantAdmin', 'TenantUser'],
  ),
})

// --- Firma listesi ve detayı (P9) ---------------------------------------------------------
// Liste firmanın, sorgular kişinin: ikisi de aynı iki role açık, ayrımı sunucu yapıyor.

const companiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/companies',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/companies/CompanyListPage'),
      'CompanyListPage',
    ),
    ['TenantAdmin', 'TenantUser'],
  ),
})

const companyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/companies/$companyId',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/companies/CompanyDetailPage'),
      'CompanyDetailPage',
    ),
    ['TenantAdmin', 'TenantUser'],
  ),
})

const portalCreditsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/credits',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/portal/credits/PortalCreditsPage'),
      'PortalCreditsPage',
    ),

    // Bakiye ve ledger müşterinin parası; ek kredi talebini sunucu yalnız tenant admin'den
    // kabul ediyor (P6 §Ek Kredi Talep Et) ve ekran o düğmeyi role göre çiziyor.
    ['TenantAdmin', 'TenantUser'],
  ),
})

// --- Operatörün okuma ekranları (A5/A6/A8) -----------------------------------------------
// Detay rotaları listeden sonra tanımlanır; `$trackingNo` ve `$companyId` bir şeye benzemeyen
// kimlikler olduğu için "new" gibi bir çakışma yok, sıra yalnız okunurluk için.

const adminQueriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/queries',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/queries/AdminQueryListPage'),
      'AdminQueryListPage',
    ),
    ['SuperAdmin'],
  ),
})

const adminQueryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/queries/$trackingNo',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/queries/AdminQueryDetailPage'),
      'AdminQueryDetailPage',
    ),
    ['SuperAdmin'],
  ),
})

const adminCompaniesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/companies',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/companies/AdminCompanyListPage'),
      'AdminCompanyListPage',
    ),
    ['SuperAdmin'],
  ),
})

const adminCompanyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/companies/$companyId',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/companies/AdminCompanyDetailPage'),
      'AdminCompanyDetailPage',
    ),
    ['SuperAdmin'],
  ),
})

const adminMersisRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/mersis',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/mersis/MersisHealthPage'),
      'MersisHealthPage',
    ),
    ['SuperAdmin'],
  ),
})

const requestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/requests',
  component: guarded(
    lazyRouteComponent(() => import('@/features/admin/requests/RequestsPage'), 'RequestsPage'),
    ['SuperAdmin'],
  ),
})

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/pricing',
  component: guarded(
    lazyRouteComponent(() => import('@/features/admin/pricing/PricingPage'), 'PricingPage'),
    ['SuperAdmin'],
  ),
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings',
  component: guarded(
    lazyRouteComponent(() => import('@/features/admin/settings/SettingsPage'), 'SettingsPage'),
    ['SuperAdmin'],
  ),
})

const rateLimitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/rate-limits',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/rate-limits/RateLimitsPage'),
      'RateLimitsPage',
    ),
    ['SuperAdmin'],
  ),
})

const auditLogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/audit-logs',
  component: guarded(
    lazyRouteComponent(() => import('@/features/admin/audit/AuditLogPage'), 'AuditLogPage'),
    ['SuperAdmin'],
  ),
})

const superAdminsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/super-admins',
  component: guarded(
    lazyRouteComponent(
      () => import('@/features/admin/super-admins/SuperAdminsPage'),
      'SuperAdminsPage',
    ),
    ['SuperAdmin'],
  ),
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  setPasswordRoute,
  changePasswordRoute,
  twoFactorSetupRoute,
  profileRoute,
  forbiddenRoute,
  maintenanceRoute,
  portalRoute,
  portalUsersRoute,
  portalUserDetailRoute,
  portalApiClientsRoute,
  queryHistoryRoute,
  newQueryRoute,
  queryDetailRoute,
  batchesRoute,
  batchDetailRoute,
  companiesRoute,
  companyDetailRoute,
  portalCreditsRoute,
  accountRequestRoute,
  adminRoute,
  tenantsRoute,
  createTenantRoute,
  tenantDetailRoute,
  adminQueriesRoute,
  adminQueryDetailRoute,
  adminCompaniesRoute,
  adminCompanyDetailRoute,
  adminMersisRoute,
  requestsRoute,
  pricingRoute,
  settingsRoute,
  rateLimitsRoute,
  auditLogRoute,
  superAdminsRoute,
])

export function createAppRouter() {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultNotFoundComponent: NotFound,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
